import type { ApiClient } from "../auth/apiClient";
import {
    RoutingRuleListResponseSchema,
    RoutingRuleSchema,
    RoutingTagKeysResponseSchema,
    RoutingTestResponseSchema,
} from "./types";
import type {
    RoutingRule,
    RoutingRuleListResponse,
    RoutingTagKeysResponse,
    RoutingTestResponse,
} from "./types";

export async function fetchRoutingRules(
    client: ApiClient,
    workflowKind?: string,
): Promise<RoutingRuleListResponse> {
    const path =
        workflowKind !== undefined && workflowKind.length > 0
            ? `/routing-rules?workflowKind=${encodeURIComponent(workflowKind)}`
            : "/routing-rules";

    const response = await client.fetch(path);
    if (!response.ok) {
        throw new Error(
            `Failed to fetch routing rules: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return RoutingRuleListResponseSchema.parse(json);
}

export async function fetchRoutingRule(
    client: ApiClient,
    id: string,
): Promise<RoutingRule> {
    const response = await client.fetch(`/routing-rules/${encodeURIComponent(id)}`);
    if (!response.ok) {
        throw new Error(
            `Failed to fetch routing rule ${id}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return RoutingRuleSchema.parse(json);
}

export async function fetchRoutingTagKeys(
    client: ApiClient,
): Promise<RoutingTagKeysResponse> {
    const response = await client.fetch("/routing-rules/tag-keys");
    if (!response.ok) {
        throw new Error(
            `Failed to fetch routing tag keys: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return RoutingTagKeysResponseSchema.parse(json);
}

export interface CreateRoutingRuleInput {
    readonly workflowKind: string;
    readonly entityType?: string | null;
    readonly taskType?: string | null;
    readonly tagPredicates: Record<string, string>;
    readonly targetQueueId: string;
    readonly description: string;
}

export async function createRoutingRule(
    client: ApiClient,
    input: CreateRoutingRuleInput,
): Promise<RoutingRule> {
    const body: Record<string, unknown> = {
        workflowKind: input.workflowKind,
        tagPredicates: input.tagPredicates,
        targetQueueId: input.targetQueueId,
        description: input.description,
    };
    if (input.entityType !== undefined && input.entityType !== null && input.entityType.length > 0) {
        body.entityType = input.entityType;
    }
    if (input.taskType !== undefined && input.taskType !== null && input.taskType.length > 0) {
        body.taskType = input.taskType;
    }

    const response = await client.fetch("/routing-rules", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw new Error(
            `Failed to create routing rule: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return RoutingRuleSchema.parse(json);
}

export interface UpdateRoutingRuleInput {
    readonly id: string;
    readonly entityType?: string | null;
    readonly taskType?: string | null;
    readonly tagPredicates: Record<string, string>;
    readonly targetQueueId: string;
    readonly description: string;
}

export async function updateRoutingRule(
    client: ApiClient,
    input: UpdateRoutingRuleInput,
): Promise<RoutingRule> {
    const body: Record<string, unknown> = {
        tagPredicates: input.tagPredicates,
        targetQueueId: input.targetQueueId,
        description: input.description,
    };
    if (input.entityType !== undefined && input.entityType !== null && input.entityType.length > 0) {
        body.entityType = input.entityType;
    }
    // Send taskType explicitly even when null so an admin can clear a
    // previously-set TaskType scope (revert to "any TaskType"). The
    // backend treats null and missing identically, but being explicit
    // here keeps the intent observable in audit logs and network traces.
    if (input.taskType !== undefined) {
        body.taskType = input.taskType !== null && input.taskType.length > 0 ? input.taskType : null;
    }

    const response = await client.fetch(`/routing-rules/${encodeURIComponent(input.id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw new Error(
            `Failed to update routing rule ${input.id}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return RoutingRuleSchema.parse(json);
}

export interface SetRoutingRuleEnabledInput {
    readonly id: string;
    readonly enabled: boolean;
}

export async function setRoutingRuleEnabled(
    client: ApiClient,
    input: SetRoutingRuleEnabledInput,
): Promise<RoutingRule> {
    const response = await client.fetch(`/routing-rules/${encodeURIComponent(input.id)}/enabled`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: input.enabled }),
    });
    if (!response.ok) {
        throw new Error(
            `Failed to toggle routing rule ${input.id}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return RoutingRuleSchema.parse(json);
}

export async function deleteRoutingRule(
    client: ApiClient,
    id: string,
): Promise<void> {
    const response = await client.fetch(`/routing-rules/${encodeURIComponent(id)}`, {
        method: "DELETE",
    });
    if (!response.ok) {
        throw new Error(
            `Failed to delete routing rule ${id}: ${String(response.status)} ${response.statusText}`,
        );
    }
}

export interface TestRoutingInput {
    readonly workflowKind: string;
    readonly entityType: string;
    readonly taskType?: string | null;
    readonly routingTags: Record<string, string>;
}

export async function testRouting(
    client: ApiClient,
    input: TestRoutingInput,
): Promise<RoutingTestResponse> {
    const body: Record<string, unknown> = {
        workflowKind: input.workflowKind,
        entityType: input.entityType,
        routingTags: input.routingTags,
    };
    if (input.taskType !== undefined && input.taskType !== null && input.taskType.length > 0) {
        body.taskType = input.taskType;
    }
    const response = await client.fetch("/routing-rules/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw new Error(
            `Failed to test routing: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return RoutingTestResponseSchema.parse(json);
}
